import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { applyDefaultSearchParams } from "../utils/listFilters";
import useModuleListConfig from "../../../Hooks/useModuleListConfig";

const FILTER_MODULE = "organization_project_projects";

function ProjectList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();
  const { config: listConfig } = useModuleListConfig(FILTER_MODULE);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const ViewFunction = (obj) => {
    navigate(`/project/view/${obj?.id}`);
  };

  const InfoFunction = (obj) => {
    navigate(`/project/info/${obj?.id}`);
  };

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const { params: currentParams, needsUpdate } = applyDefaultSearchParams(
      searchParams,
      listConfig
    );

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Projects_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchProjectsWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${MAIN_URL}/api/project-management/projects?per_page=all&organization_id=${orgId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const rawData = result.data ?? result ?? [];
      const dataArray = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
        ? rawData.data
        : [];

      const normalized = dataArray.map((item) => {
        const clientLabel =
          item.client_name ??
          item.client?.client_name ??
          item.organization_client_label ??
          (typeof item.organization_client === "string"
            ? item.organization_client
            : null) ??
          "-";
        const categoryLabel =
          item.category_name ??
          item.project_category_name ??
          item.category?.project_category_name ??
          item.project_category_label ??
          (typeof item.project_category === "string"
            ? item.project_category
            : null) ??
          "-";
        const subcategoryLabel =
          item.subcategory_name ??
          item.project_subcategory_name ??
          item.sub_category?.project_subcategory_name ??
          item.project_subcategory_label ??
          (typeof item.project_subcategory === "string"
            ? item.project_subcategory
            : null) ??
          "-";
        const typeLabel =
          item.type_name ??
          item.project_type_name ??
          item.type?.project_type_name ??
          item.project_type_label ??
          (typeof item.project_type === "string" ? item.project_type : null) ??
          "-";
        const projectLeadLabel =
          item.project_lead_name ??
          item.project_manager?.person?.full_name ??
          item.project_lead_label ??
          (typeof item.project_lead === "string" ? item.project_lead : null) ??
          "-";

        return {
          ...item,
          id: item.organization_project_id ?? item.id,
          client_name: clientLabel,
          category_name: categoryLabel,
          subcategory_name: subcategoryLabel,
          type_name: typeLabel,
          project_lead_name: projectLeadLabel,
          organization_client: clientLabel,
          project_category: categoryLabel,
          project_subcategory: subcategoryLabel,
          project_type: typeLabel,
          project_lead: projectLeadLabel,
          organization_client_id: clientLabel,
          organization_project_category_id: categoryLabel,
          organization_project_subcategory_id: subcategoryLabel,
          organization_project_type_id: typeLabel,
          project_lead_organization_person_id: projectLeadLabel,
          source: item.project_source ?? null,
          allocated_hours: item.estimated_hours ?? item.allocated_hours ?? null,
          allocated_hours_period:
            item.estimated_hours_period ?? item.allocated_hours_period ?? null,
          client: clientLabel,
          type: typeLabel,
          category: categoryLabel,
          subcategory: subcategoryLabel,
          lead: projectLeadLabel,
        };
      });

      // Client-side search
      const search = searchParams.get("search");
      let filtered = normalized;
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        filtered = normalized.filter(
          (item) =>
            String(item.project_name ?? "").toLowerCase().includes(q) ||
            String(item.client_name ?? "").toLowerCase().includes(q) ||
            String(item.category_name ?? "").toLowerCase().includes(q) ||
            String(item.project_status ?? "").toLowerCase().includes(q)
        );
      }

      // Client-side sort
      const defaultSort = listConfig?.defaultSort || {};
      const sortBy = searchParams.get("sortBy") || defaultSort.field || "project_name";
      const sortOrder = searchParams.get("sortOrder") || defaultSort.direction || "asc";
      filtered = [...filtered].sort((a, b) => {
        const va = a[sortBy] ?? "";
        const vb = b[sortBy] ?? "";
        const cmp = String(va).localeCompare(String(vb), undefined, {
          numeric: true,
        });
        return sortOrder === "desc" ? -cmp : cmp;
      });

      // Client-side pagination
      const page = Number.parseInt(searchParams.get("page"), 10) || 1;
      const pageSize = Math.min(
        Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        100
      );
      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const start = (page - 1) * pageSize;
      const paged = filtered.slice(start, start + pageSize);

      setProjects(paged);
      setPaginationData({
        page,
        pageSize,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      });
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching projects:", err);
      toast.error("Failed to fetch projects");
      setProjects([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, searchParams, listConfig]);

  useEffect(() => {
    fetchProjectsWithFilters();
  }, [fetchProjectsWithFilters]);

  return (
    <>
      <Layout4
      heading="Projects"
        Route="/project"
      btnName="Add Projects"
        showAddButton={true}
      add_action="ADD_ORGANIATION_PROJECT"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          viewFunction={(obj) => ViewFunction(obj)}
          infoFunction={(obj) => InfoFunction(obj)}
          gridKey="Projects_grid"
          tableName="organization_project_projects"
          filterModule={FILTER_MODULE}
          primaryKey="id"
          heading="organization_project_projects"
          data={projects}
          sortname={"project_name"}
          showActions={true}
          Route="/project"
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={[
            "EDIT_ORGANIZATION_PROJECT",
            "DELETE_ORGANIZATION_PROJECT",
          ]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default ProjectList;
